import React, { useState, useEffect } from 'react';
import FocusTrap from 'focus-trap-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import useIsMobile from '../hooks/useIsMobile';
import './GoogleClassroomImport.css';
import {
  School as SchoolIcon,
  Close as CloseIcon,
  CloudDownload as ImportIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

interface GoogleCourse {
  google_classroom_id: string;
  name: string;
  description: string;
  section: string;
  room: string;
  enrollment_code: string;
  course_state: string;
  is_teacher: boolean;
  is_student: boolean;
  already_imported: boolean;
}

interface GoogleStudent {
  google_classroom_id: string;
  google_classroom_course_id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  photo_url?: string;
  already_imported: boolean;
}

interface GoogleClassroomImportProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'classes' | 'students';
  courseId?: string; // Required when type is 'students'
  courseName?: string;
  onImportComplete: () => void;
}

const GoogleClassroomImport: React.FC<GoogleClassroomImportProps> = ({
  isOpen,
  onClose,
  type,
  courseId,
  courseName,
  onImportComplete
}) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { isMobile } = useIsMobile();
  const [step, setStep] = useState<'auth' | 'preview' | 'importing' | 'complete'>('auth');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Course data
  const [courses, setCourses] = useState<GoogleCourse[]>([]);
  const [selectedCourses, setSelectedCourses] = useState<string[]>([]);
  
  // Student data
  const [students, setStudents] = useState<GoogleStudent[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  
  // Internal state for tracking selected course when importing students
  const [internalSelectedCourseId, setInternalSelectedCourseId] = useState<string | null>(null);
  const [internalSelectedCourseName, setInternalSelectedCourseName] = useState<string | null>(null);
  
  // Import results
  const [importResults, setImportResults] = useState<any>(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep('auth');
      setError(null);
      setCourses([]);
      setSelectedCourses([]);
      setStudents([]);
      setSelectedStudents([]);
      setInternalSelectedCourseId(null);
      setInternalSelectedCourseName(null);
      setImportResults(null);
    }
  }, [isOpen]);

  // Check Google Classroom connection status
  useEffect(() => {
    if (isOpen && step === 'auth') {
      checkGoogleClassroomStatus();
    }
  }, [isOpen, step]);

  const checkGoogleClassroomStatus = async () => {
    try {
      setLoading(true);
      
      // Check if user has Google Classroom connected
      const response = await fetch('/api/auth/google-classroom/status', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.connected) {
        // User is already connected, proceed to load data
        try {
          await loadGoogleClassroomData();
        } catch (loadError: any) {
          // If loading fails, it might be due to auth issues not caught by status
          // Check if it's an auth error and show auth prompt
          if (loadError.message && 
              (loadError.message.toLowerCase().includes('authentication') || 
               loadError.message.toLowerCase().includes('expired') ||
               loadError.message.toLowerCase().includes('revoked') ||
               loadError.message.toLowerCase().includes('invalid_grant'))) {
            showGoogleAuthPrompt();
          } else {
            // Some other error - propagate it
            throw loadError;
          }
        }
      } else {
        // User needs to connect Google Classroom
        showGoogleAuthPrompt();
      }
    } catch (err) {
      console.error('Error checking Google Classroom status:', err);
      showGoogleAuthPrompt();
    } finally {
      setLoading(false);
    }
  };

  const showGoogleAuthPrompt = () => {
    setError(null); // Clear any existing errors
    // This will show the OAuth connect button
  };

  const handleGoogleAuth = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Initiate OAuth flow
      const response = await fetch('/api/auth/google-classroom/connect', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success && data.authorization_url) {
        // Redirect user to Google OAuth
        window.location.href = data.authorization_url;
      } else {
        throw new Error(data.error || 'Failed to initiate OAuth flow');
      }
      
    } catch (err: any) {
      setError(err.message || 'Failed to authenticate with Google Classroom');
    } finally {
      setLoading(false);
    }
  };

  const loadGoogleClassroomData = async () => {
    try {
      setLoading(true);
      
      if (type === 'classes') {
        await loadCourses();
      } else if (type === 'students') {
        // If no courseId provided, load courses first so user can select one
        if (!courseId) {
          await loadCourses();
        } else {
          await loadStudents(courseId);
        }
      }
      
      setStep('preview');
    } catch (err: any) {
      setError(err.message || 'Failed to load Google Classroom data');
    } finally {
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    const response = await fetch('/api/google-classroom/courses', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      // Check if this is an authentication error
      if (response.status === 400 && errorData.error && 
          (errorData.error.toLowerCase().includes('authentication') || 
           errorData.error.toLowerCase().includes('expired') ||
           errorData.error.toLowerCase().includes('revoked') ||
           errorData.error.toLowerCase().includes('invalid_grant'))) {
        // Authentication error - show auth prompt instead of generic error
        showGoogleAuthPrompt();
        return;
      }
      throw new Error(errorData.error || 'Failed to fetch courses');
    }

    const data = await response.json();
    if (!data.success) {
      // Backend returned success: false - check if it's an auth error
      if (data.error && 
          (data.error.toLowerCase().includes('authentication') || 
           data.error.toLowerCase().includes('expired') ||
           data.error.toLowerCase().includes('revoked') ||
           data.error.toLowerCase().includes('invalid_grant'))) {
        // Authentication error - show auth prompt instead of generic error
        showGoogleAuthPrompt();
        return;
      }
      throw new Error(data.error || 'Failed to fetch courses');
    }
    
    setCourses(data.courses || []);
  };

  const loadStudents = async (courseId: string) => {
    const response = await fetch(`/api/google-classroom/courses/${courseId}/students`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch students');
    }

    const data = await response.json();
    setStudents(data.students || []);
  };

  const handleCourseSelection = (selectedCourseId: string) => {
    if (type === 'students' && !courseId && !internalSelectedCourseId) {
      // For student import without courseId, selecting a course should load students from that course
      handleCourseSelectionForStudentImport(selectedCourseId);
    } else {
      // Normal course selection for class import
      if (selectedCourses.includes(selectedCourseId)) {
        setSelectedCourses(selectedCourses.filter(id => id !== selectedCourseId));
      } else {
        setSelectedCourses([...selectedCourses, selectedCourseId]);
      }
    }
  };

  const handleCourseSelectionForStudentImport = async (selectedCourseId: string) => {
    try {
      setLoading(true);
      
      // Find the selected course to get its name
      const selectedCourse = courses.find(course => course.google_classroom_id === selectedCourseId);
      
      // Update internal state to track the selected course
      setInternalSelectedCourseId(selectedCourseId);
      setInternalSelectedCourseName(selectedCourse?.name || null);
      
      // Load students from the selected course
      await loadStudents(selectedCourseId);
      
    } catch (err: any) {
      setError(err.message || 'Failed to load students from selected course');
    } finally {
      setLoading(false);
    }
  };

  const handleStudentSelection = (studentId: string) => {
    if (selectedStudents.includes(studentId)) {
      setSelectedStudents(selectedStudents.filter(id => id !== studentId));
    } else {
      setSelectedStudents([...selectedStudents, studentId]);
    }
  };

  const handleSelectAll = () => {
    if (type === 'classes') {
      const availableCourses = courses.filter(course => !course.already_imported);
      if (selectedCourses.length === availableCourses.length) {
        setSelectedCourses([]);
      } else {
        setSelectedCourses(availableCourses.map(course => course.google_classroom_id));
      }
    } else {
      const availableStudents = students.filter(student => !student.already_imported);
      if (selectedStudents.length === availableStudents.length) {
        setSelectedStudents([]);
      } else {
        setSelectedStudents(availableStudents.map(student => student.google_classroom_id));
      }
    }
  };

  const handleImport = async () => {
    try {
      setStep('importing');
      setLoading(true);
      setError(null);

      let response;
      
      if (type === 'classes') {
        response = await fetch('/api/google-classroom/import/courses', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            course_ids: selectedCourses
          })
        });
      } else {
        const selectedStudentData = students.filter(student => 
          selectedStudents.includes(student.google_classroom_id)
        );
        
        response = await fetch('/api/google-classroom/import/students', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            students: selectedStudentData
          })
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Import failed');
      }

      const results = await response.json();
      setImportResults(results);
      setStep('complete');
      
    } catch (err: any) {
      setError(err.message || 'Import failed');
      setStep('preview');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    onImportComplete();
    onClose();
  };

  if (!isOpen) return null;

  // Use internal selected course ID if no courseId prop is provided
  const effectiveCourseId = courseId || internalSelectedCourseId;
  const effectiveCourseName = courseName || internalSelectedCourseName;

  const availableItems = type === 'classes' 
    ? courses.filter(course => !course.already_imported)
    : type === 'students' && !effectiveCourseId
    ? courses  // Show ALL courses for student import, not just non-imported ones
    : students.filter(student => !student.already_imported);
    
  const selectedItems = type === 'classes' || (type === 'students' && !effectiveCourseId) ? selectedCourses : selectedStudents;

  return (
    <FocusTrap
      focusTrapOptions={{
        allowOutsideClick: true,
        escapeDeactivates: true
      }}
    >
      <div className="google-classroom-modal" role="presentation">
        <div
          className={`google-classroom-modal-content ${isMobile ? 'google-classroom-modal-fullscreen' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="google-classroom-modal-title"
        >
          <div className="google-classroom-modal-header">
            <h2 id="google-classroom-modal-title">
              <div className="google-classroom-logo">
                <SchoolIcon aria-hidden="true" />
              </div>
              Import from Google Classroom
              {type === 'students' && effectiveCourseName && (
                <span style={{ fontSize: '1rem', opacity: 0.9 }}>
                  - {effectiveCourseName}
                </span>
              )}
            </h2>
            <button 
              className="google-classroom-modal-close" 
              onClick={onClose}
              aria-label="Close dialog"
            >
              <CloseIcon aria-hidden="true" />
            </button>
          </div>

        <div className="google-classroom-modal-body">
          {error && (
            <div className="google-classroom-error">
              <ErrorIcon />
              {error}
            </div>
          )}

          {/* Step 1: Authentication */}
          {step === 'auth' && (
            <div className="google-classroom-step">
              <h3>
                <span className="step-number">1</span>
                Connect to Google Classroom
              </h3>
              
              {loading ? (
                <div className="google-classroom-loading">
                  <div className="spinner"></div>
                  Checking Google Classroom connection...
                </div>
              ) : (
                <div>
                  <p>To import your {type}, you need to connect your Google Classroom account with additional permissions for accessing courses and rosters.</p>
                  <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '12px' }}>
                    You'll be redirected to Google to grant the necessary permissions for accessing your Google Classroom data.
                  </p>
                  <button 
                    className="btn-google-classroom primary"
                    onClick={handleGoogleAuth}
                    style={{ marginTop: '16px' }}
                  >
                    <ImportIcon />
                    Connect Google Classroom
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Preview and Selection */}
          {step === 'preview' && (
            <div className="google-classroom-step">
              <h3>
                <span className="step-number">2</span>
                Select {type === 'students' && !effectiveCourseId ? 'Course' : type} to Import
                {type === 'students' && !effectiveCourseId && (
                  <div style={{ fontSize: '0.875rem', fontWeight: 'normal', marginTop: '8px', opacity: 0.8 }}>
                    Select any Google Classroom course to import students from (regardless of whether the class is already imported)
                  </div>
                )}
                {type === 'students' && effectiveCourseId && !courseId && (
                  <div style={{ fontSize: '0.875rem', fontWeight: 'normal', marginTop: '8px', opacity: 0.8 }}>
                    Importing students from: {effectiveCourseName}
                    <button 
                      onClick={() => {
                        setInternalSelectedCourseId(null);
                        setInternalSelectedCourseName(null);
                        setStudents([]);
                        setSelectedStudents([]);
                      }}
                      style={{ 
                        marginLeft: '12px', 
                        fontSize: '0.75rem', 
                        padding: '4px 8px', 
                        background: 'transparent', 
                        border: '1px solid #6b7280', 
                        borderRadius: '4px',
                        color: '#6b7280',
                        cursor: 'pointer'
                      }}
                    >
                      Change Course
                    </button>
                  </div>
                )}
              </h3>

              {loading ? (
                <div className="google-classroom-loading">
                  <div className="spinner"></div>
                  Loading {type} from Google Classroom...
                </div>
              ) : (
                <>
                  <div className="classroom-import-summary">
                    <h4>Import Summary</h4>
                    <div className="import-stats">
                      <div className="import-stat">
                        <div className="number">{availableItems.length}</div>
                        <div className="label">Available</div>
                      </div>
                      <div className="import-stat">
                        <div className="number">{selectedItems.length}</div>
                        <div className="label">Selected</div>
                      </div>
                      <div className="import-stat">
                        <div className="number">
                          {type === 'classes' ? courses.length - availableItems.length : students.length - availableItems.length}
                        </div>
                        <div className="label">Already Imported</div>
                      </div>
                    </div>
                  </div>

                  {availableItems.length > 0 && (type === 'classes' || (type === 'students' && effectiveCourseId)) && (
                    <button 
                      className="btn-google-classroom secondary"
                      onClick={handleSelectAll}
                      style={{ marginBottom: '16px' }}
                    >
                      {selectedItems.length === availableItems.length ? 'Deselect All' : 'Select All'}
                    </button>
                  )}

                  <div className="classroom-items-grid">
                    {(type === 'classes' || (type === 'students' && !effectiveCourseId)) ? (
                      courses.map(course => (
                        <div
                          key={course.google_classroom_id}
                          className={`classroom-item ${
                            selectedCourses.includes(course.google_classroom_id) ? 'selected' : ''
                          } ${course.already_imported && type === 'classes' ? 'already-imported' : ''}`}
                          onClick={() => {
                            // For class import, don't allow selecting already imported courses
                            if (type === 'classes' && course.already_imported) return;
                            // For student import, allow selecting any course
                            handleCourseSelection(course.google_classroom_id);
                          }}
                        >
                          <div className="classroom-item-header">
                            {!course.already_imported && type === 'classes' && (
                              <div className="classroom-item-checkbox">
                                <input
                                  type="checkbox"
                                  checked={selectedCourses.includes(course.google_classroom_id)}
                                  onChange={() => handleCourseSelection(course.google_classroom_id)}
                                  onClick={e => e.stopPropagation()}
                                />
                              </div>
                            )}
                            {type === 'students' && !effectiveCourseId && (
                              <div className="classroom-item-info" style={{ paddingLeft: '16px' }}>
                                <span style={{ fontSize: '0.875rem', color: '#10b981', fontWeight: '600' }}>
                                  Click to import students →
                                </span>
                              </div>
                            )}
                            <div className="classroom-item-info">
                              <h4>{course.name}</h4>
                              {course.description && <p>{course.description}</p>}
                            </div>
                          </div>
                          <div className="classroom-item-meta">
                            {course.section && (
                              <span className="classroom-meta-tag">
                                Section: {course.section}
                              </span>
                            )}
                            {course.room && (
                              <span className="classroom-meta-tag">
                                Room: {course.room}
                              </span>
                            )}
                            {course.is_teacher && (
                              <span className="classroom-meta-tag teacher">Teacher</span>
                            )}
                            {course.is_student && (
                              <span className="classroom-meta-tag student">Student</span>
                            )}
                            {course.already_imported && type === 'classes' && (
                              <span className="classroom-meta-tag already-imported">
                                <CheckIcon style={{ fontSize: '12px', marginRight: '4px' }} />
                                Class Already Imported
                              </span>
                            )}
                            {course.already_imported && type === 'students' && !effectiveCourseId && (
                              <span className="classroom-meta-tag" style={{ backgroundColor: '#e3f2fd', color: '#1976d2' }}>
                                <CheckIcon style={{ fontSize: '12px', marginRight: '4px' }} />
                                Class Imported (Students Available)
                              </span>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      students.map(student => (
                        <div
                          key={student.google_classroom_id}
                          className={`classroom-item ${
                            selectedStudents.includes(student.google_classroom_id) ? 'selected' : ''
                          } ${student.already_imported ? 'already-imported' : ''}`}
                          onClick={() => !student.already_imported && handleStudentSelection(student.google_classroom_id)}
                        >
                          <div className="classroom-item-header">
                            {!student.already_imported && (
                              <div className="classroom-item-checkbox">
                                <input
                                  type="checkbox"
                                  checked={selectedStudents.includes(student.google_classroom_id)}
                                  onChange={() => handleStudentSelection(student.google_classroom_id)}
                                  onClick={e => e.stopPropagation()}
                                />
                              </div>
                            )}
                            <div className="classroom-item-info">
                              <h4>{student.full_name || `${student.first_name} ${student.last_name}`}</h4>
                              {student.email && <p>{student.email}</p>}
                            </div>
                          </div>
                          <div className="classroom-item-meta">
                            {student.already_imported && (
                              <span className="classroom-meta-tag already-imported">
                                <CheckIcon style={{ fontSize: '12px', marginRight: '4px' }} />
                                Already Imported
                              </span>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {availableItems.length === 0 && type === 'classes' && (
                    <div style={{ textAlign: 'center', color: '#666', padding: '40px 20px' }}>
                      <WarningIcon style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
                      <p>No new classes found to import.</p>
                      <p>All classes from your Google Classroom have already been imported.</p>
                    </div>
                  )}
                  
                  {availableItems.length === 0 && type === 'students' && !effectiveCourseId && (
                    <div style={{ textAlign: 'center', color: '#666', padding: '40px 20px' }}>
                      <WarningIcon style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
                      <p>No courses found in your Google Classroom.</p>
                      <p>You need courses with students to import student data.</p>
                    </div>
                  )}
                  
                  {availableItems.length === 0 && type === 'students' && effectiveCourseId && (
                    <div style={{ textAlign: 'center', color: '#666', padding: '40px 20px' }}>
                      <WarningIcon style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
                      <p>No new students found to import.</p>
                      <p>All students from this course have already been imported.</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Step 3: Importing */}
          {step === 'importing' && (
            <div className="google-classroom-step">
              <h3>
                <span className="step-number">3</span>
                Importing {type}...
              </h3>
              <div className="google-classroom-loading">
                <div className="spinner"></div>
                Importing {selectedItems.length} {type} from Google Classroom...
                <p style={{ marginTop: '12px', fontSize: '0.875rem' }}>
                  Please don't close this window while the import is in progress.
                </p>
              </div>
            </div>
          )}

          {/* Step 4: Complete */}
          {step === 'complete' && importResults && (
            <div className="google-classroom-step">
              <h3>
                <span className="step-number">✓</span>
                Import Complete
              </h3>
              
              <div className="google-classroom-success">
                <CheckIcon />
                Successfully imported {importResults.counts?.imported || 0} {type}!
              </div>

              <div className="classroom-import-summary">
                <h4>Import Results</h4>
                <div className="import-stats">
                  <div className="import-stat">
                    <div className="number" style={{ color: '#10b981' }}>
                      {importResults.counts?.imported || 0}
                    </div>
                    <div className="label">Imported</div>
                  </div>
                  <div className="import-stat">
                    <div className="number" style={{ color: '#f59e0b' }}>
                      {importResults.counts?.skipped || 0}
                    </div>
                    <div className="label">Skipped</div>
                  </div>
                  <div className="import-stat">
                    <div className="number" style={{ color: '#ef4444' }}>
                      {importResults.counts?.errors || 0}
                    </div>
                    <div className="label">Errors</div>
                  </div>
                </div>
              </div>

              {importResults.errors && importResults.errors.length > 0 && (
                <div className="google-classroom-error">
                  <ErrorIcon />
                  <div>
                    <strong>Some items couldn't be imported:</strong>
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                      {importResults.errors.map((error: any, index: number) => (
                        <li key={index}>{error.error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="google-classroom-modal-footer">
          {step === 'preview' && (
            <>
              <button 
                className="btn-google-classroom secondary" 
                onClick={onClose}
              >
                {t('modals.cancel')}
              </button>
              {/* Only show import button for classes or students with a courseId */}
              {(type === 'classes' || (type === 'students' && effectiveCourseId)) && (
                <button 
                  className="btn-google-classroom primary" 
                  onClick={handleImport}
                  disabled={selectedItems.length === 0}
                >
                  <ImportIcon />
                  Import {selectedItems.length} {type === 'classes' ? 'Classes' : 'Students'}
                </button>
              )}
            </>
          )}
          
          {step === 'complete' && (
            <button 
              className="btn-google-classroom primary" 
              onClick={handleComplete}
            >
              <CheckIcon />
              Done
            </button>
          )}
          
          {(step === 'auth' || step === 'importing') && (
            <button 
              className="btn-google-classroom secondary" 
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
    </FocusTrap>
  );
};

export default GoogleClassroomImport;
